/**
 * ATTENDANCE CONTEXTS — one marking UI, the context is a parameter.
 *
 * Dan, 2026-09-24: "Attendance in camps, clinics, and events can work exactly
 * the same way as attendance in a class (program/slot) — normalize the
 * attendance marking for them TO the main method and pass a type of
 * class/event/camp/clinic."
 *
 * The backend (Phase D1) holds every mark in `attendance_records` with a
 * `context_type`; `services/attendanceRecords.CONTEXT_CAPABILITIES` is its
 * table of what each context may do. This is the frontend twin: the ONE
 * roster (AttendanceRoster) reads these flags, never the context name, so a
 * capability is turned on here and nowhere else (Dan, 2026-09-12: "evaluate
 * what should get turned on via parameters").
 *
 * Every flag is either DRIFT resolved to the more capable side (then it is the
 * same for every context that shares it) or a FORK where each context keeps
 * its behavior from before D2. The comparison that produced this table is in
 * the backend's docs/EVENT_PRIMITIVE_CONSOLIDATION.md, "D2 — frontend".
 * Both sides of every fork are pinned in
 * components/attendance/__tests__/attendanceContexts.test.tsx.
 */

export type AttendanceContextType = "class" | "event" | "camp" | "clinic";

/** The mark a roster row shows. `no_record` = not marked (an event's `expected`). */
export type RosterStatus = "present" | "absent" | "makeup" | "no_record";

/** What a row's buttons ask the context to do. */
export type RosterAction = "present" | "absent" | "clear" | "check_out";

export interface AttendanceCapabilities {
  /** "Made up at …" on an absence discharged by a same-day make-up. Class only (D1). */
  makeups: boolean;
  /**
   * "Mark All Present". Class only, as before D2. Saves each row through the
   * roster's `save` (2026-09-24 — before that it changed the screen only).
   */
  bulkMarkPresent: boolean;
  /** Clicking a row opens the athlete details modal. Class only (an event row may be a guest). */
  rowDetails: boolean;
  /** Check-in / check-out stamps under the name and a Check Out action. Event contexts. */
  checkInOut: boolean;
  /** A QR scanner entry point. Event contexts. */
  scanner: boolean;
  /**
   * Clicking Present on a present row clears it. DRIFT → every context: the
   * class delete, the events undo-check-in, and the camp/clinic undo-check-in
   * added for D2.
   */
  clearPresent: boolean;
  /**
   * Clicking Absent on an absent row clears it. FORK: the class deletes the
   * mark; no event endpoint clears an absence (whether a no-show can be
   * reversed is an open question for Dan, backend services/eventAttendance).
   */
  clearAbsent: boolean;
  /**
   * Present ↔ Absent directly, without clearing first. FORK: the class and the
   * per-day writers (camps, clinics) accept it; the single check-in model
   * (events) refuses a no-show over a check-in and a check-in over a no-show.
   */
  switchPresentAbsent: boolean;
  /**
   * An absence is terminal (the events `no_show`): Absent asks for
   * confirmation first — the events page always did — and Present is disabled
   * on an absent row.
   */
  absentIsFinal: boolean;
  /** A counts strip above the roster. Event contexts. */
  summary: boolean;
  /** Name search + status filter. DRIFT → every event context (events had it; camps/clinics did not). */
  search: boolean;
}

export const ATTENDANCE_CONTEXT_CAPABILITIES: Readonly<Record<AttendanceContextType, Readonly<AttendanceCapabilities>>> =
  Object.freeze({
    class: Object.freeze({
      makeups: true,
      bulkMarkPresent: true,
      rowDetails: true,
      checkInOut: false,
      scanner: false,
      clearPresent: true,
      clearAbsent: true,
      switchPresentAbsent: true,
      absentIsFinal: false,
      summary: false,
      search: false,
    }),
    event: Object.freeze({
      makeups: false,
      bulkMarkPresent: false,
      rowDetails: false,
      checkInOut: true,
      scanner: true,
      clearPresent: true,
      clearAbsent: false,
      switchPresentAbsent: false,
      absentIsFinal: true,
      summary: true,
      search: true,
    }),
    camp: Object.freeze({
      makeups: false,
      bulkMarkPresent: false,
      rowDetails: false,
      checkInOut: true,
      scanner: true,
      clearPresent: true,
      clearAbsent: false,
      switchPresentAbsent: true,
      absentIsFinal: false,
      summary: true,
      search: true,
    }),
    clinic: Object.freeze({
      makeups: false,
      bulkMarkPresent: false,
      rowDetails: false,
      checkInOut: true,
      scanner: true,
      clearPresent: true,
      clearAbsent: false,
      switchPresentAbsent: true,
      absentIsFinal: false,
      summary: true,
      search: true,
    }),
  });

export function capabilitiesFor(context: AttendanceContextType): Readonly<AttendanceCapabilities> {
  const caps = ATTENDANCE_CONTEXT_CAPABILITIES[context];
  if (!caps) throw new Error(`attendanceContexts: unknown attendance context "${context}"`);
  return caps;
}

/**
 * May a row showing `from` take a click on `button`? The ONE rule every
 * context's buttons follow; a refused click is a disabled button, never a
 * request the backend will turn down.
 */
export function isMarkAllowed(
  caps: Readonly<AttendanceCapabilities>,
  from: RosterStatus | undefined,
  button: "present" | "absent"
): boolean {
  const current = from ?? "no_record";
  if (current === button) {
    return button === "present" ? caps.clearPresent : caps.clearAbsent;
  }
  if (current === "absent" && button === "present") {
    return !caps.absentIsFinal && caps.switchPresentAbsent;
  }
  if (current === "present" && button === "absent") {
    return caps.switchPresentAbsent;
  }
  return true;
}
